/**
 * Google Tag Manager integration utilities
 *
 * GTM is initialized globally at app startup (in index.tsx) and gated by
 * the REACT_APP_GTM_ID env var + production build check.
 * Individual pages push virtualPageView events via trackGTMPageView().
 */

declare global {
  interface Window {
    dataLayer?: Record<string, unknown>[];
  }
}

let gtmInitialized = false;

/**
 * Initialize Google Tag Manager by injecting the GTM script.
 * Only loads in production builds when a GTM ID is configured.
 *
 * @returns true if GTM was initialized, false otherwise
 */
export const initGTM = (): boolean => {
  if (process.env.NODE_ENV !== 'production') {
    console.log('[GTM] Skipped - not in production');
    return false;
  }

  const gtmId = process.env.REACT_APP_GTM_ID;
  if (!gtmId) {
    console.log('[GTM] Skipped - no GTM ID configured');
    return false;
  }

  if (gtmInitialized) {
    return true;
  }

  try {
    // Initialize the dataLayer
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      'gtm.start': new Date().getTime(),
      event: 'gtm.js',
    });

    // Load the GTM script
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(gtmId)}`;

    document.head.appendChild(script);

    gtmInitialized = true;
    return true;
  } catch (error) {
    console.warn('[GTM] Failed to initialize:', error);
    return false;
  }
};

/**
 * Push a virtual page view event to the GTM dataLayer.
 * Call this on SPA route changes where GTM should track a page view.
 *
 * @param pagePath - The page path (e.g., '/verify-email')
 * @param pageTitle - The page title (e.g., 'Verify Email')
 */
export const trackGTMPageView = (pagePath: string, pageTitle: string): void => {
  if (!window.dataLayer) {
    return;
  }

  window.dataLayer.push({
    event: 'virtualPageView',
    pagePath,
    pageTitle,
  });
};

/**
 * Push a signup conversion event to the GTM dataLayer.
 * Fired once after a new user completes the signup flow and lands in the app.
 * GTM should have a trigger for this event that fires a Google Ads Conversion tag.
 *
 * This relies on the Conversion Linker tag reading the gclid cookie from the
 * user's browser — the cookie persists through the Auth0 redirect round-trip
 * since both the landing page and post-signup app are on the same domain.
 */
export const trackGTMSignupConversion = (): void => {
  if (!window.dataLayer) {
    return;
  }

  window.dataLayer.push({
    event: 'signupComplete',
  });

  console.log('[GTM] Pushed signupComplete conversion event to dataLayer');
};
